import { AnimatedSection } from "@/components/common/AnimatedSection"
import { SectionLabel } from "@/components/common/SectionLabel"
import { Button } from "@/components/ui/Button"
import { Input } from "@/components/ui/Input"
import { Select } from "@/components/ui/Select"
import { useSEO } from "@/hooks/useSEO"
import { createFileRoute, Link } from "@tanstack/react-router"
import { ArrowRight, FileText, Mail, MessageCircle } from "lucide-react"

export const Route = createFileRoute("/contact")({
  component: ContactPage,
})

const CONTACT_OPTIONS = [
  {
    icon: MessageCircle,
    color: "bg-blue-light",
    iconColor: "text-blue",
    title: "Talk to Sales",
    description: "Discuss your business needs and get a custom pricing quote from our team.",
    action: "Email sales",
    href: "mailto:business@orchestrate.global",
  },
  {
    icon: Mail,
    color: "bg-green-light",
    iconColor: "text-green-DEFAULT",
    title: "Technical Support",
    description: "Get help from our engineers with integration, API, or platform questions.",
    action: "Email support",
    href: "mailto:support@orchestrate.global",
  },
  {
    icon: FileText,
    color: "bg-amber-light",
    iconColor: "text-amber-DEFAULT",
    title: "Documentation",
    description: "Read our API docs, SDK guides, and integration tutorials.",
    action: "Browse docs",
    href: "https://status.orchestrate.global",
  },
]

const VOLUME_OPTIONS = [
  { value: "", label: "Select monthly volume" },
  { value: "under-100k", label: "Under $100K/month" },
  { value: "100k-1m", label: "$100K – $1M/month" },
  { value: "1m-10m", label: "$1M – $10M/month" },
  { value: "10m-50m", label: "$10M – $50M/month" },
  { value: "over-50m", label: "Over $50M/month" },
]

function ContactPage() {
  useSEO({
    title: "Contact",
    description:
      "Get in touch with the Orchestrate team. Talk to sales, get technical support, or explore our documentation.",
    canonical: "https://orchestrate.global/contact",
  })

  return (
    <>
      <section className="bg-surface grid-bg relative overflow-hidden pt-32 pb-24">
        <div className="hero-glow" aria-hidden="true" />
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid items-start gap-16 lg:grid-cols-2">
            {/* Left: contact options */}
            <div>
              <AnimatedSection>
                <SectionLabel label="Get in touch" className="mb-5" />
              </AnimatedSection>
              <AnimatedSection delay={0.1}>
                <h1 className="font-display mb-6 text-5xl leading-tight tracking-tight sm:text-6xl lg:text-7xl">
                  Let's build
                  <br />
                  <em className="text-blue not-italic">something great.</em>
                </h1>
              </AnimatedSection>
              <AnimatedSection delay={0.2}>
                <p className="text-muted mb-10 text-xl leading-relaxed">
                  Whether you're exploring payment orchestration or ready to go live, we're here to
                  help every step of the way.
                </p>
              </AnimatedSection>

              <div className="space-y-4">
                {CONTACT_OPTIONS.map((opt, i) => {
                  const Icon = opt.icon
                  const isExternal = opt.href.startsWith("mailto:")
                  const commonProps = {
                    className:
                      "flex gap-4 p-5 bg-white rounded-2xl border border-border hover:-translate-y-1 hover:shadow-xl hover:shadow-ink/8 transition-all duration-250 group w-full text-left",
                    "aria-label": opt.action,
                  }

                  const content = (
                    <>
                      <div
                        className={`h-11 w-11 ${opt.color} flex flex-shrink-0 items-center justify-center rounded-xl`}
                      >
                        <Icon className={`h-5 w-5 ${opt.iconColor}`} aria-hidden="true" />
                      </div>
                      <div className="flex-1">
                        <h3 className="mb-1 text-sm font-semibold">{opt.title}</h3>
                        <p className="text-muted text-sm leading-relaxed">{opt.description}</p>
                        <span className="text-blue mt-2 inline-block text-xs font-semibold">
                          {opt.action} →
                        </span>
                      </div>
                      <ArrowRight
                        className="text-muted group-hover:text-blue h-4 w-4 flex-shrink-0 self-center transition-all duration-150 group-hover:translate-x-0.5"
                        aria-hidden="true"
                      />
                    </>
                  )

                  return (
                    <AnimatedSection key={opt.title} delay={0.3 + i * 0.1}>
                      {isExternal ? (
                        <a href={opt.href} {...commonProps}>
                          {content}
                        </a>
                      ) : (
                        <Link to={opt.href} {...commonProps}>
                          {content}
                        </Link>
                      )}
                    </AnimatedSection>
                  )
                })}
              </div>

              {/* Direct emails */}
              <AnimatedSection delay={0.6}>
                <div className="border-border mt-8 rounded-2xl border bg-white p-6">
                  <p className="text-muted mb-4 text-xs font-semibold tracking-widest uppercase">
                    Direct contact
                  </p>
                  <div className="space-y-3">
                    {[
                      { type: "Sales", email: "business@orchestrate.global" },
                      { type: "Support", email: "support@orchestrate.global" },
                      { type: "General", email: "engage@orchestrate.global" },
                    ].map(({ type, email }) => (
                      <div key={type} className="flex items-center justify-between">
                        <span className="text-muted text-xs font-medium">{type}</span>
                        <a
                          href={`mailto:${email}`}
                          className="text-muted hover:text-blue text-sm transition-colors"
                        >
                          {email}
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              </AnimatedSection>
            </div>

            {/* Right: Contact form */}
            <AnimatedSection direction="right" delay={0.2}>
              <div className="border-border shadow-ink/5 rounded-2xl border bg-white p-8 shadow-xl">
                <h2 className="mb-2 text-xl font-semibold">Send us a message</h2>
                <p className="text-muted mb-6 text-sm">
                  We respond to all enquiries within 1 business day.
                </p>
                <form
                  onSubmit={(e) => e.preventDefault()}
                  className="space-y-4"
                  aria-label="Contact form"
                  noValidate
                >
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Input
                      label="First name"
                      name="firstName"
                      type="text"
                      placeholder="Jane"
                      autoComplete="given-name"
                      required
                    />
                    <Input
                      label="Last name"
                      name="lastName"
                      type="text"
                      placeholder="Smith"
                      autoComplete="family-name"
                      required
                    />
                  </div>

                  <Input
                    label="Work email"
                    name="email"
                    type="email"
                    placeholder="jane@company.com"
                    autoComplete="email"
                    required
                  />

                  <Input
                    label="Company"
                    name="company"
                    type="text"
                    placeholder="Acme Inc."
                    autoComplete="organization"
                  />

                  <Select
                    label="Monthly payment volume"
                    name="volume"
                    options={VOLUME_OPTIONS}
                    placeholder="Select monthly volume"
                    defaultValue=""
                  />

                  <div>
                    <label
                      htmlFor="message"
                      className="text-muted mb-2 block text-xs font-semibold tracking-wider uppercase"
                    >
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      placeholder="Tell us about your payment needs, current stack, or any questions..."
                      className="bg-surface border-border placeholder:text-muted/60 focus:ring-blue w-full resize-none rounded-xl border px-4 py-3 text-sm transition-all focus:border-transparent focus:ring-2 focus:outline-none"
                    />
                  </div>

                  <Button type="submit" variant="primary" size="lg" className="w-full">
                    Send message <ArrowRight className="h-4 w-4" />
                  </Button>

                  <p className="text-muted text-center text-xs">
                    By submitting you agree to our{" "}
                    <Link to="/legal" className="text-blue hover:underline">
                      Privacy Policy
                    </Link>
                    . No spam, ever.
                  </p>
                </form>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </>
  )
}
